import { Component, OnDestroy, OnInit } from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ConfirmationService, MessageService } from "primeng/api";
import { AdminModule } from "../../services/admin-access.service";
import { RolesService, TeamMember } from "./roles.service";

interface ModuleGroup {
  name: string;
  modules: AdminModule[];
}

@Component({
  selector: "app-roles",
  templateUrl: "./roles.component.html",
  styleUrls: ["./roles.component.css"],
  providers: [MessageService, ConfirmationService],
})
export class RolesComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  catalogue: AdminModule[] = [];
  groups: ModuleGroup[] = [];
  members: TeamMember[] = [];

  loading = true;
  saving = false;
  search = "";
  tab: "active" | "pending" | "inactive" = "active";

  /** Null when adding; otherwise the member being edited. */
  editing: TeamMember | null = null;
  editorOpen = false;
  form = { name: "", email: "", mobile: "", isSuper: false };
  picked: { [key: string]: boolean } = {};
  formError = "";

  constructor(
    private api: RolesService,
    private messages: MessageService,
    private confirmation: ConfirmationService,
  ) {}

  ngOnInit() {
    this.api.modules().pipe(takeUntil(this.destroy$)).subscribe(
      list => {
        this.catalogue = list || [];
        this.groups = [];
        this.catalogue.forEach(m => {
          let g = this.groups.filter(x => x.name === m.group)[0];
          if (!g) { g = { name: m.group, modules: [] }; this.groups.push(g); }
          g.modules.push(m);
        });
      },
      () => this.fail("Could not load the module list."),
    );
    this.load();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load() {
    this.loading = true;
    this.api.list().pipe(takeUntil(this.destroy$)).subscribe(
      rows => { this.members = rows || []; this.loading = false; },
      () => { this.loading = false; this.fail("Could not load administrators."); },
    );
  }

  /* ── list ─────────────────────────────────────────────────────────────── */

  get visible(): TeamMember[] {
    const q = this.search.trim().toLowerCase();
    return this.members.filter(m => {
      const inTab =
        this.tab === "inactive" ? !m.isActive :
        this.tab === "pending"  ? m.isActive && m.pending :
                                  m.isActive && !m.pending;
      if (!inTab) return false;
      if (!q) return true;
      return (m.name + " " + m.email + " " + m.mobile).toLowerCase().indexOf(q) >= 0;
    });
  }

  count(tab: "active" | "pending" | "inactive"): number {
    return this.members.filter(m =>
      tab === "inactive" ? !m.isActive :
      tab === "pending"  ? m.isActive && m.pending :
                           m.isActive && !m.pending,
    ).length;
  }

  labelFor(key: string): string {
    const m = this.catalogue.filter(x => x.key === key)[0];
    return m ? m.label : key;
  }

  /** Two or three names, then a count - a full list makes the row unreadable. */
  summary(m: TeamMember): string {
    if (m.isSuper) return "Every section";
    if (!m.modules.length) return "No sections";
    const names = m.modules.map(k => this.labelFor(k));
    if (names.length <= 3) return names.join(", ");
    return names.slice(0, 3).join(", ") + " +" + (names.length - 3) + " more";
  }

  /* ── editor ───────────────────────────────────────────────────────────── */

  openNew() {
    this.editing = null;
    this.form = { name: "", email: "", mobile: "", isSuper: false };
    this.picked = { dashboard: true };
    this.formError = "";
    this.editorOpen = true;
  }

  openEdit(m: TeamMember) {
    this.editing = m;
    this.form = { name: m.name, email: m.email, mobile: m.mobile, isSuper: m.isSuper };
    this.picked = {};
    m.modules.forEach(k => (this.picked[k] = true));
    this.formError = "";
    this.editorOpen = true;
  }

  closeEditor() {
    if (this.saving) return;
    this.editorOpen = false;
    this.editing = null;
  }

  toggle(key: string) {
    this.picked[key] = !this.picked[key];
    this.formError = "";
  }

  groupAllOn(g: ModuleGroup): boolean {
    return g.modules.every(m => !!this.picked[m.key]);
  }

  toggleGroup(g: ModuleGroup) {
    const on = !this.groupAllOn(g);
    g.modules.forEach(m => (this.picked[m.key] = on));
    this.formError = "";
  }

  get selected(): string[] {
    return this.catalogue.filter(m => !!this.picked[m.key]).map(m => m.key);
  }

  save() {
    const name = (this.form.name || "").trim();
    const email = (this.form.email || "").trim();
    if (!name) { this.formError = "Enter a name."; return; }
    if (!this.editing && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      this.formError = "Enter a valid email address."; return;
    }
    if (!this.form.isSuper && !this.selected.length) {
      this.formError = "Pick at least one section."; return;
    }

    this.saving = true;
    const body = {
      name,
      mobile: (this.form.mobile || "").trim(),
      modules: this.selected,
      isSuper: !!this.form.isSuper,
    };

    if (this.editing) {
      this.api.update(this.editing.id, body).pipe(takeUntil(this.destroy$)).subscribe(
        () => { this.saving = false; this.editorOpen = false; this.ok("Access updated."); this.load(); },
        err => { this.saving = false; this.formError = this.reason(err, "Could not save."); },
      );
    } else {
      this.api.create(Object.assign({ email: email.toLowerCase() }, body)).pipe(takeUntil(this.destroy$)).subscribe(
        res => {
          this.saving = false;
          this.editorOpen = false;
          this.ok(res.emailed
            ? "Invitation sent to " + email + "."
            : "Account created, but the invitation email could not be sent. Use Resend invite.");
          this.load();
        },
        err => { this.saving = false; this.formError = this.reason(err, "Could not create the account."); },
      );
    }
  }

  /* ── row actions ──────────────────────────────────────────────────────── */

  resend(m: TeamMember) {
    this.api.resendInvite(m.id).pipe(takeUntil(this.destroy$)).subscribe(
      () => this.ok("Invitation sent to " + m.email + "."),
      err => this.fail(this.reason(err, "Could not send the invitation.")),
    );
  }

  setActive(m: TeamMember, isActive: boolean) {
    this.api.setActive(m.id, isActive).pipe(takeUntil(this.destroy$)).subscribe(
      () => { this.ok(isActive ? "Account activated." : "Account deactivated."); this.load(); },
      err => this.fail(this.reason(err, "Could not change the account.")),
    );
  }

  remove(m: TeamMember) {
    this.confirmation.confirm({
      key: "roles-confirm",
      icon: "pi pi-exclamation-triangle",
      message: "Remove " + (m.name || m.email) + "? They lose access immediately.",
      accept: () => {
        this.api.remove(m.id).pipe(takeUntil(this.destroy$)).subscribe(
          () => { this.ok("Administrator removed."); this.load(); },
          err => this.fail(this.reason(err, "Could not remove the account.")),
        );
      },
    });
  }

  /* ── helpers ──────────────────────────────────────────────────────────── */

  private reason(err: any, fallback: string): string {
    return (err && err.error && err.error.error) || fallback;
  }
  private ok(text: string) {
    this.messages.add({ key: "custom", severity: "success", summary: text });
  }
  private fail(text: string) {
    this.messages.add({ key: "custom", severity: "error", summary: text });
  }
}
